/**
 * Profile Guards
 *
 * Protect routes based on the user's BUSINESS PROFILE (carrier / shipper),
 * derived from client.ofertaLance and client.ofertaFrete flags via PermissionService.
 *
 * These are intentionally SEPARATE from:
 *   • authGuard     — checks only whether the user is authenticated (JWT valid)
 *   • roleGuard     — checks the `user.role` string field (legacy)
 *
 * ─── Available guards ────────────────────────────────────────────
 *
 *   carrierGuard              → requires ofertaLance = true
 *   shipperGuard              → requires ofertaFrete = true
 *   authenticatedProfileGuard → requires carrier OR shipper (shared features)
 *
 * ─── Usage ───────────────────────────────────────────────────────
 *
 *   { path: 'carriers',  canActivate: [carrierGuard],              ... }
 *   { path: 'shipper',   canActivate: [shipperGuard],              ... }
 *   { path: 'payments',  canActivate: [authenticatedProfileGuard], ... }
 *
 * No route.data[] configuration is needed — permissions are read
 * directly from PermissionService computed signals (synchronous,
 * in-memory, zero overhead).
 */
import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn, ActivatedRouteSnapshot, Router } from '@angular/router';
import { PermissionService } from '../auth/permission.service';
import { UserType } from '../../shared/ui/models/private-sidebar.types';

/** Allow access only to users with the carrier profile (ofertaLance = true). */
export const carrierGuard: CanActivateFn = (): boolean => {
  const permission = inject(PermissionService);
  const router = inject(Router);

  if (permission.isCarrier()) return true;

  router.navigate(['/access-denied']);
  return false;
};

/** Allow access only to users with the shipper profile (ofertaFrete = true). */
export const shipperGuard: CanActivateFn = (): boolean => {
  const permission = inject(PermissionService);
  const router = inject(Router);

  if (permission.isShipper()) return true;

  router.navigate(['/access-denied']);
  return false;
};

/**
 * Allow access to users with EITHER carrier OR shipper profile.
 * Used for shared features visible to both sides (payments, iaassist).
 */
export const authenticatedProfileGuard: CanActivateFn = (): boolean => {
  const permission = inject(PermissionService);
  const router = inject(Router);

  if (permission.isCarrier() || permission.isShipper()) return true;

  router.navigate(['/access-denied']);
  return false;
};

/**
 * Child route guard for shipper sub-routes.
 *
 * Admin and Standard users always have access to all shipper routes.
 * Restricted profiles (order, requester, approver) require the child route
 * to declare `route.data['allowedShipperProfiles']` containing their profile.
 * Routes without `allowedShipperProfiles` are blocked for restricted profiles.
 *
 * Apply via `canActivateChild` on the `/app/shipper` and
 * `/app/configurations-shipper` parent routes in app.routes.ts.
 */
export const shipperUserTypeGuard: CanActivateChildFn = (
  route: ActivatedRouteSnapshot
): boolean => {
  const permission = inject(PermissionService);
  const router = inject(Router);

  // Admin and Standard always have full access
  if (permission.isAdmin() || permission.isStandard() || permission.isApprover()) return true;

  // Restricted profiles need explicit allowance per route
  const allowedProfiles = route.data['allowedShipperProfiles'] as UserType[] | undefined;

  if (!allowedProfiles?.length) {
    router.navigate(['/access-denied']);
    return false;
  }

  const hasAccess =
    (allowedProfiles.includes('order') && permission.isOrder()) ||
    (allowedProfiles.includes('requester') && permission.isRequester())

  if (hasAccess) return true;

  router.navigate(['/access-denied']);
  return false;
};
